<template>
  <div class="bcb-campaign-create container-fluid">
    <!-- Header -->
    <div class="d-flex align-items-center my-6">
      <button class="btn btn-icon btn-light mr-3" @click="$router.push({ name: 'bcb-campaign' })">
        <i class="fas fa-arrow-left"></i>
      </button>
      <div>
        <h4 class="text-dark fw-bold mb-1">{{ isEdit ? 'Chỉnh sửa chiến dịch' : 'Tạo chiến dịch mới' }}</h4>
        <p class="text-muted m-0">BCB Bundle Campaign</p>
      </div>
    </div>

    <b-overlay :show="loading">
      <div class="bg-white rounded border p-6">
        <!-- Tabs -->
        <b-tabs v-model="activeTab">
          <!-- Tab 1: Thông tin chung -->
          <b-tab title="Thông tin chung" :active="activeTab === 0">
            <div class="pt-5">
              <div class="row">
                <div class="col-md-6">
                  <basic-input
                    label="Tên chiến dịch (*)"
                    placeholder="Nhập tên chiến dịch"
                    name="name"
                    :value.sync="form.name"
                    :state="validated && !form.name ? false : null"
                    invalid-feedback="Tên chiến dịch không được để trống."
                    required
                  />
                </div>
                <div class="col-md-6">
                  <BasicSelect
                    label="Phòng khám đối tác"
                    placeholder="Chọn một hoặc nhiều phòng khám"
                    name="partnerId"
                    :options="partnerOptions"
                    track-by="id"
                    value-label="name"
                    :multiple="true"
                    :change-value-by-object="true"
                    :value.sync="form.partnerIds"
                  />
                </div>
              </div>
              <div class="row">
                <div class="col-md-6">
                  <BasicDatePicker
                    label="Ngày bắt đầu khám"
                    placeholder="Chọn ngày bắt đầu"
                    name="startDate"
                    format="YYYY-MM-DD"
                    value-type="format"
                    :value.sync="form.startDate"
                  />
                </div>
                <div class="col-md-6">
                  <BasicDatePicker
                    label="Ngày bắt đầu gửi tin nhắn"
                    placeholder="Chọn ngày bắt đầu gửi tin nhắn (tuỳ chọn)"
                    name="notifyDate"
                    format="YYYY-MM-DD"
                    value-type="format"
                    :value.sync="form.notifyDate"
                  />
                </div>
              </div>
              <div class="row">
                <div class="col-md-6">
                  <BasicDatePicker
                    label="Ngày đóng đặt lịch hẹn"
                    placeholder="Chọn mốc chặn đặt lịch (tuỳ chọn)"
                    name="minBookingDate"
                    format="YYYY-MM-DD"
                    value-type="format"
                    :value.sync="form.minBookingDate"
                  />
                </div>
              </div>
              <div class="row">
                <div class="col-md-12">
                  <basic-text-area
                    label="Mô tả"
                    placeholder="Nhập mô tả chiến dịch..."
                    name="description"
                    :value.sync="form.description"
                    :rows="4"
                  />
                </div>
              </div>

              <div class="d-flex justify-content-end mt-4">
                <button class="btn btn-light mr-3" @click="$router.push({ name: 'bcb-campaign' })">Huỷ</button>
                <LoadingButton
                  class="btn btn-success mr-3"
                  :handler="save"
                >
                  {{ isEdit ? 'Lưu thay đổi' : 'Tạo chiến dịch' }}
                </LoadingButton>
                <LoadingButton
                  v-if="isEdit && form.status === 1"
                  class="btn btn-primary"
                  :handler="toggleStatus"
                >
                  <i class="fas fa-play mr-2"></i>Bắt đầu chiến dịch
                </LoadingButton>
                <LoadingButton
                  v-if="isEdit && form.status === 2"
                  class="btn btn-danger"
                  :handler="toggleStatus"
                >
                  <i class="fas fa-stop mr-2"></i>Dừng chiến dịch
                </LoadingButton>
              </div>
            </div>
          </b-tab>

          <!-- Tab 2: Danh sách KH (chỉ khi edit) -->
          <b-tab title="Danh sách KH" :disabled="!isEdit">
            <div class="pt-5">
              <p v-if="!isEdit" class="text-muted">Vui lòng lưu chiến dịch trước khi xem danh sách khách hàng.</p>
              <div v-else>
                <div class="d-flex justify-content-end mb-4">
                  <LoadingButton class="btn btn-outline-success mr-2" :handler="exportCustomers">
                    <i class="fas fa-file-export mr-2"></i>Xuất danh sách khách hàng
                  </LoadingButton>
                  <button v-if="isInsertPermission" class="btn btn-outline-success" @click="$bvModal.show('modal-bcb-campaign-customer-import')">
                    <i class="fas fa-file-excel mr-2"></i>Import KH từ Excel
                  </button>
                </div>
                <bcb-campaign-customer-import
                  :campaign-id="campaignId"
                  @import-success="loadCustomers"
                />
                <b-overlay :show="customerLoading">
                  <template-table
                    :column="customerColumns"
                    :data="customers"
                    :paging="customerPaging"
                    :loading="customerLoading"
                    :select-action="false"
                    :search-action="false"
                    :table-action="true"
                    @change-page="onCustomerChangePage"
                  >
                    <template #body="{ item }">
                      <td>{{ item.fullName || '—' }}</td>
                      <td>{{ item.phone || '—' }}</td>
                      <td>{{ item.email || '—' }}</td>
                      <td>{{ item.partnerName || '—' }}</td>
                      <td>{{ item.companyName || '—' }}</td>
                      <td>{{ item.department || '—' }}</td>
                      <td class="text-center">{{ item.gender === 1 ? 'Nam' : item.gender === 0 ? 'Nữ' : '—' }}</td>
                      <td>{{ item.dateOfBirth ? formatDate(item.dateOfBirth) : '—' }}</td>
                      <td>{{ item.noteImport || '—' }}</td>
                      <td>
                        <span class="status-badge" :style="getCustomerStatusStyle(item.status)">
                          {{ getCustomerStatusLabel(item.status) }}
                        </span>
                      </td>
                    </template>
                    <template #action="{ item }">
                      <action :value="item" :show_view="false" :show_edit="false" :show_delete="false">
                        <template #append>
                          <button
                            class="btn btn-sm btn-icon btn-light-primary ml-1"
                            title="Chi tiết"
                            @click="viewCustomer(item)"
                          >
                            <i class="fas fa-eye text-primary" />
                          </button>
                          <button
                            v-if="isDeletePermission"
                            class="btn btn-sm btn-icon btn-light-danger ml-1"
                            title="Xóa"
                            :disabled="deletingId === item.id"
                            @click="confirmDeleteCustomer(item)"
                          >
                            <b-spinner v-if="deletingId === item.id" small />
                            <i v-else class="fas fa-trash-alt text-danger" />
                          </button>
                        </template>
                      </action>
                    </template>
                  </template-table>
                </b-overlay>
              </div>
            </div>
          </b-tab>

          <!-- Tab 3: Gửi thông báo (chỉ khi edit) -->
          <b-tab title="Gửi thông báo" :disabled="!isEdit">
            <div class="pt-5">
              <p v-if="!isEdit" class="text-muted">Vui lòng lưu chiến dịch trước khi gửi thông báo.</p>
              <bcb-campaign-send-notification v-else ref="sendNotification" :campaign-id="campaignId" />
            </div>
          </b-tab>

          <!-- Tab 4: Chỉ định người phụ trách (TS-18, chỉ khi edit) -->
          <b-tab title="Chỉ định người phụ trách" :disabled="!isEdit">
            <div class="pt-5">
              <p v-if="!isEdit" class="text-muted">Vui lòng lưu chiến dịch trước khi chỉ định người phụ trách.</p>
              <bcb-campaign-assign-reviewers v-else ref="assignReviewers" :campaign-id="campaignId" />
            </div>
          </b-tab>
        </b-tabs>
      </div>
    </b-overlay>
  </div>
</template>

<script>
import { CUSTOMER_STATUS } from '../constants/index.js';
import BcbCampaignCustomerImport from './BcbCampaignCustomerImport.vue';
import BcbCampaignSendNotification from './BcbCampaignSendNotification.vue';
import BcbCampaignAssignReviewers from './BcbCampaignAssignReviewers.vue';

export default {
  name: 'BcbCampaignCreate',
  components: { BcbCampaignCustomerImport, BcbCampaignSendNotification, BcbCampaignAssignReviewers },
  props: {
    id: { type: [String, Number], default: null },
  },
  data() {
    return {
      loading: false,
      validated: false,
      customerLoading: false,
      activeTab: 0,
      partnerOptions: [],
      form: {
        name: '',
        partnerIds: [],
        startDate: '',
        notifyDate: '',
        minBookingDate: '',
        description: '',
        status: null,
      },
      customers: [],
      customerPaging: { page: 1, pageSize: 10, total: 0 },
      deletingId: null,
      customerColumns: [
        { key: 'fullName', label: 'Họ và tên' },
        { key: 'phone', label: 'Số điện thoại' },
        { key: 'email', label: 'Email' },
        { key: 'partnerName', label: 'Phòng khám' },
        { key: 'companyName', label: 'Công ty' },
        { key: 'department', label: 'Phòng ban' },
        { key: 'gender', label: 'Giới tính' },
        { key: 'dateOfBirth', label: 'Ngày sinh' },
        { key: 'noteImport', label: 'Ghi chú' },
        { key: 'status', label: 'Trạng thái' },
      ],
    };
  },
  computed: {
    isEdit() {
      return !!this.campaignId;
    },
    campaignId() {
      return this.id || this.$route.params.id || null;
    },
  },
  watch: {
    activeTab(tab) {
      // Tab index 1 = Danh sách KH
      if (tab === 1 && this.isEdit) {
        this.loadCustomers();
      }
      if (tab === 2 && this.isEdit && this.$refs.sendNotification) {
        this.$refs.sendNotification.initialize();
      }
      // Tab index 3 = Chỉ định người phụ trách (TS-18)
      if (tab === 3 && this.isEdit && this.$refs.assignReviewers) {
        this.$refs.assignReviewers.initialize();
      }
    },
    'customerPaging.page'() { this.loadCustomers(); },
    'customerPaging.pageSize'() { this.customerPaging.page = 1; this.loadCustomers(); },
  },
  async created() {
    await this.loadPartners();
    if (this.isEdit) {
      this.loadCampaign();
    }
  },
  methods: {
    async loadPartners() {
      try {
        const params = { page: 1, pageSize: 1000, isActive: true };
        const res = await this.$api.get('BcbPartner', { params });
        const raw = res?.data ?? res;
        this.partnerOptions = raw?.items ?? raw?.data?.items ?? (Array.isArray(raw) ? raw : []);
      } catch (e) {
        // silent fail
      }
    },
    async loadCampaign() {
      this.loading = true;
      try {
        const res = await this.$api.get(`BcbCampaign/${this.campaignId}`);
        const data = res?.data ?? res;
        this.form.name = data.name || '';
        this.form.partnerIds = this.mapSelectedPartners(data);
        this.form.description = data.description || '';
        this.form.status = data.status ?? null;
        this.form.startDate = data.startDate
          ? this.$moment.unix(data.startDate).utcOffset('+07:00').format('YYYY-MM-DD')
          : '';
        this.form.notifyDate = data.notifyDate
          ? this.$moment.unix(data.notifyDate).utcOffset('+07:00').format('YYYY-MM-DD')
          : '';
        this.form.minBookingDate = data.minBookingDate
          ? this.$moment.unix(data.minBookingDate).utcOffset('+07:00').format('YYYY-MM-DD')
          : '';
      } catch (e) {
        this.$toastr.e({ title: 'Lỗi', msg: e?.message || 'Không thể tải thông tin chiến dịch.' });
      } finally {
        this.loading = false;
      }
    },
    mapSelectedPartners(data) {
      const selectedFromDetail = Array.isArray(data?.partners) ? data.partners : [];
      const selectedIds = Array.isArray(data?.partnerIds)
        ? data.partnerIds
        : (data?.partnerId ? [data.partnerId] : []);

      const partnerMap = new Map();
      [...this.partnerOptions, ...selectedFromDetail].forEach((partner) => {
        if (partner?.id) {
          partnerMap.set(partner.id, partner);
        }
      });

      return selectedIds
        .map((id) => partnerMap.get(id) || selectedFromDetail.find((partner) => partner?.id === id))
        .filter(Boolean);
    },
    getSelectedPartnerId(partner) {
      return partner?.id ?? partner;
    },
    appendSelectedPartners(formData) {
      const partnerIds = this.form.partnerIds
        .map((partner) => this.getSelectedPartnerId(partner))
        .filter(Boolean);

      partnerIds.forEach((id) => formData.append('partnerIds', id));
      formData.append('partnerId', partnerIds[0] ?? null);
    },

    validateNotificationDates() {
      if (!this.form.startDate) {
        this.$toastr.e({ title: 'Loi', msg: 'Vui long chon ngay bat dau chien dich.' });
        return false;
      }

      if (!this.form.notifyDate) return true;

      const startDate = this.$moment(this.form.startDate, 'YYYY-MM-DD').startOf('day');
      const notifyDate = this.$moment(this.form.notifyDate, 'YYYY-MM-DD').startOf('day');
      const earliestNotifyDate = startDate.clone().subtract(10, 'days');

      if (!notifyDate.isBefore(startDate, 'day')) {
        this.$toastr.e({ title: 'Loi', msg: 'Ngay bat dau gui tin nhan phai truoc ngay bat dau chien dich.' });
        return false;
      }

      if (notifyDate.isBefore(earliestNotifyDate, 'day')) {
        this.$toastr.e({ title: 'Loi', msg: 'Ngay bat dau gui tin nhan khong duoc som hon 10 ngay truoc chien dich.' });
        return false;
      }

      return true;
    },

    async save() {
      this.validated = true;
      if (!this.form.name || !this.form.partnerIds.length || !this.validateNotificationDates()) return;

      try {
        const formData = new FormData();
        formData.append('name', this.form.name);
        this.appendSelectedPartners(formData);
        formData.append('description', this.form.description);
        formData.append('startDate', this.form.startDate
          ? this.convertDateToTimestamp(this.form.startDate, 'YYYY-MM-DD')
          : null);
        if(this.form.notifyDate != '' && this.form.notifyDate != null) {
            formData.append('notifyDate', this.form.notifyDate
          ? this.convertDateToTimestamp(this.form.notifyDate, 'YYYY-MM-DD')
          : null);
        }
        if (this.form.minBookingDate) {
          formData.append('minBookingDate', this.convertDateToTimestamp(this.form.minBookingDate, 'YYYY-MM-DD'));
        }


        if (this.isEdit) {
          await this.$api.put(`BcbCampaign/${this.campaignId}`, formData);
          this.$toastr.s({ title: 'Thành công', msg: 'Đã cập nhật chiến dịch.' });
        } else {
          const res = await this.$api.post('BcbCampaign', formData);
          this.$toastr.s({ title: 'Thành công', msg: 'Đã tạo chiến dịch.' });
          const newId = (res?.data ?? res)?.id;
          if (newId) {
            this.$router.replace({ name: 'bcb-campaign-edit', params: { id: newId } });
          } else {
            this.$router.push({ name: 'bcb-campaign' });
          }
        }
      } catch (e) {
        this.$toastr.e({ title: 'Lỗi', msg: e?.message || 'Không thể lưu chiến dịch.' });
      }
    },
    async toggleStatus() {
      // status 1 = Nháp, status 2 = Đang diễn ra
      const newStatus = this.form.status === 2 ? 1 : 2;
      if (!this.validateNotificationDates()) return;
      try {
        const formData = new FormData();
        formData.append('name', this.form.name);
        this.appendSelectedPartners(formData);
        formData.append('description', this.form.description);
        formData.append('startDate', this.form.startDate
          ? this.convertDateToTimestamp(this.form.startDate, 'YYYY-MM-DD')
          : null);
       if(this.form.notifyDate != '') {
         formData.append('notifyDate', this.form.notifyDate
          ? this.convertDateToTimestamp(this.form.notifyDate, 'YYYY-MM-DD')
          : null);
       }
        if (this.form.minBookingDate) {
          formData.append('minBookingDate', this.convertDateToTimestamp(this.form.minBookingDate, 'YYYY-MM-DD'));
        }
        formData.append('status', newStatus);
        await this.$api.put(`BcbCampaign/${this.campaignId}`, formData);
        this.form.status = newStatus;
        const msg = newStatus === 2 ? 'Đã bắt đầu chiến dịch.' : 'Đã dừng chiến dịch.';
        this.$toastr.s({ title: 'Thành công', msg });
      } catch (e) {
        this.$toastr.e({ title: 'Lỗi', msg: e?.message || 'Không thể cập nhật trạng thái.' });
      }
    },
    async loadCustomers() {
      if (!this.campaignId) return;
      this.customerLoading = true;
      try {
        const res = await this.$api.get('BcbCampaignCustomer', {
          params: {
            campaignId: this.campaignId,
            page: this.customerPaging.page,
            pageSize: this.customerPaging.pageSize,
          },
        });
        const raw = res?.data ?? res;
        this.customers = raw?.items ?? raw?.data?.items ?? (Array.isArray(raw) ? raw : []);
        this.customerPaging.total = raw?.total ?? raw?.data?.total ?? this.customers.length;
      } catch (e) {
        this.$toastr.e({ title: 'Lỗi', msg: e?.message || 'Không thể tải danh sách khách hàng.' });
      } finally {
        this.customerLoading = false;
      }
    },
    onCustomerChangePage(page) {
      this.customerPaging.page = page;
      this.loadCustomers();
    },
    async exportCustomers() {
      try {
        const res = await this.$api.get('BcbCampaignCustomer/export', {
          params: { campaignId: this.campaignId },
          responseType: 'blob',
        });
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'DanhSachKhachHang.xlsx');
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (e) {
        this.$toastr.e({ title: 'Lỗi', msg: e?.message || 'Không thể xuất danh sách khách hàng.' });
      }
    },
    viewCustomer(item) {
      const customerId = item.id || item.customerId || item.bcbCampaignCustomerId;
      if (!customerId) {
        this.$toastr.e({ title: 'Lá»—i', msg: 'KhÃ´ng tÃ¬m tháº¥y ID khÃ¡ch hÃ ng.' });
        return;
      }
      this.$router.push({
        name: 'bcb-campaign-customer-detail',
        params: { campaignId: String(this.campaignId), customerId: String(customerId) },
      });
    },
    confirmDeleteCustomer(item) {
      this.$bvModal
        .msgBoxConfirm(
          `Bạn có chắc muốn xóa khách hàng "${item.fullName || item.phone || ''}" không?`,
          {
            title: 'Xác nhận xóa',
            okTitle: 'Xóa',
            cancelTitle: 'Hủy',
            okVariant: 'danger',
            centered: true,
          },
        )
        .then((confirmed) => {
          if (confirmed) this.deleteCustomer(item.id);
        });
    },
    async deleteCustomer(id) {
      try {
        this.deletingId = id;
        await this.$api.delete(`/BcbCampaignCustomer/${id}`);
        this.$toastr.s({ title: 'Thành công', msg: 'Đã xóa khách hàng' });
        await this.loadCustomers();
      } catch (err) {
        this.$toastr.e({ title: 'Lỗi!', msg: err?.message || 'Xóa thất bại' });
      } finally {
        this.deletingId = null;
      }
    },
    getCustomerStatusLabel(status) {
      return CUSTOMER_STATUS[status]?.label || '—';
    },
    getCustomerStatusStyle(status) {
      const cfg = CUSTOMER_STATUS[status];
      return {
        backgroundColor: cfg?.bg || '#F1EFE8',
        color: cfg?.color || '#444441',
      };
    },
    formatDate(val) {
      if (!val) return '—';
      if (typeof val === 'number') {
        return this.$moment.unix(val).utcOffset('+07:00').format('DD/MM/YYYY');
      }
      return this.$moment(val).format('DD/MM/YYYY');
    },
  },
};
</script>

<style scoped lang="scss">
.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 0.85rem;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
}
</style>
